import { useEffect } from 'react'
import { SupportedLanguage, SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE } from '../types/i18n.ts'

/**
 * Apex bez www — provjereno: https://www.weddingcamera.app 301-redirecta na
 * https://weddingcamera.app. Canonical i hreflang moraju pokazivati na konačni
 * URL, inače Search Console blog rute prijavljuje kao "Page with redirect".
 *
 * Build prerenderira javne rute s njihovim vlastitim canonicalom.
 */
export const SITE_URL = 'https://weddingcamera.app'

/** og:locale očekuje pun locale kod, ne samo jezik. */
const OG_LOCALES: Record<SupportedLanguage, string> = {
	hr: 'hr_HR',
	sr: 'sr_RS',
	bs: 'bs_BA',
	sl: 'sl_SI',
	de: 'de_DE',
	en: 'en_US'
}

export interface SeoAlternate {
	lang: SupportedLanguage
	/** Putanja bez domene, npr. '/de/blog/hochzeitsfotos-sammeln'. */
	path: string
}

export interface SeoOptions {
	title: string
	description: string
	/** Putanja trenutne rute bez domene, npr. '/blog/qr-kod-za-vjencanje'. */
	path: string
	lang: SupportedLanguage
	image?: string
	type?: 'website' | 'article'
	alternates?: SeoAlternate[]
	jsonLd?: Array<Record<string, unknown>>
	/** Za stranice koje ne smiju u indeks (npr. nepostojeći članak). */
	noindex?: boolean
}

type Undo = () => void

const absolute = (url: string): string => {
	if (!url) return ''
	if (url.startsWith('http://') || url.startsWith('https://')) return url
	return `${SITE_URL}${url.startsWith('/') ? url : `/${url}`}`
}

/**
 * Postavlja content na postojeći tag i vraća funkciju koja vraća staru vrijednost.
 * Ako tag ne postoji, stvara ga i označava s data-seo="dynamic".
 *
 * Razlog za ovakav pristup: početni HTML već nosi description,
 * og:* i twitter:* tagove. Da samo dodajemo nove, stranica bi imala dva
 * description taga i dva canonicala — što crawleri tumače kao grešku.
 */
const setAttrOn = (
	selector: string,
	create: () => HTMLElement,
	attr: string,
	value: string
): Undo => {
	const existing = document.head.querySelector<HTMLElement>(selector)

	if (existing) {
		const previous = existing.getAttribute(attr)
		existing.setAttribute(attr, value)

		return () => {
			if (previous === null) existing.removeAttribute(attr)
			else existing.setAttribute(attr, previous)
		}
	}

	const created = create()
	created.setAttribute(attr, value)
	created.setAttribute('data-seo', 'dynamic')
	document.head.appendChild(created)

	return () => created.remove()
}

const meta = (key: 'name' | 'property', keyValue: string, content: string): Undo =>
	setAttrOn(
		`meta[${key}="${keyValue}"]`,
		() => {
			const el = document.createElement('meta')
			el.setAttribute(key, keyValue)
			return el
		},
		'content',
		content
	)

const canonical = (href: string): Undo =>
	setAttrOn(
		'link[rel="canonical"]',
		() => {
			const el = document.createElement('link')
			el.setAttribute('rel', 'canonical')
			return el
		},
		'href',
		href
	)

const appendFresh = (el: HTMLElement): Undo => {
	el.setAttribute('data-seo', 'dynamic')
	document.head.appendChild(el)
	return () => el.remove()
}

/**
 * Per-route <head> upravljanje bez react-helmeta.
 *
 * Snapshot tagove preuzima prvi mount. Svaka ruta zatim upravlja svojim
 * tagovima i uklanja ih pri unmountu, uključujući hreflang i JSON-LD.
 */
export const useSeo = (options: SeoOptions): void => {
	const serialized = JSON.stringify(options)

	useEffect(() => {
		// Adopt the build snapshot once; subsequent navigation owns its own tags.
		document.head.querySelectorAll('[data-seo="prerender"]').forEach(el => el.remove())
		const {
			title,
			description,
			path,
			lang,
			image,
			type = 'website',
			alternates = [],
			jsonLd = [],
			noindex = false
		} = JSON.parse(serialized) as SeoOptions

		const url = absolute(path)
		const imageUrl = image ? absolute(image) : ''
		const undos: Undo[] = []

		const previousTitle = document.title
		document.title = title
		undos.push(() => { document.title = previousTitle })

		// Public content determines the document language. Legal pages are
		// English even though their canonical URLs have no language prefix.
		const previousLang = document.documentElement.lang
		document.documentElement.lang = lang
		undos.push(() => { document.documentElement.lang = previousLang })

		undos.push(meta('name', 'description', description))
		undos.push(canonical(url))
		undos.push(meta('name', 'robots', noindex ? 'noindex, follow' : 'index, follow'))

		undos.push(meta('property', 'og:title', title))
		undos.push(meta('property', 'og:description', description))
		undos.push(meta('property', 'og:url', url))
		undos.push(meta('property', 'og:type', type))
		undos.push(meta('property', 'og:site_name', 'Wedding Camera'))
		undos.push(meta('property', 'og:locale', OG_LOCALES[lang] ?? OG_LOCALES[DEFAULT_LANGUAGE]))
		if (imageUrl) undos.push(meta('property', 'og:image', imageUrl))

		undos.push(meta('name', 'twitter:card', 'summary_large_image'))
		undos.push(meta('name', 'twitter:title', title))
		undos.push(meta('name', 'twitter:description', description))
		if (imageUrl) undos.push(meta('name', 'twitter:image', imageUrl))

		// hreflang alternate — ne postoji u index.html, uvijek se stvara iznova
		alternates.forEach(alternate => {
			const el = document.createElement('link')
			el.setAttribute('rel', 'alternate')
			el.setAttribute('hreflang', alternate.lang)
			el.setAttribute('href', absolute(alternate.path))
			undos.push(appendFresh(el))
		})

		const fallback = alternates.find(a => a.lang === DEFAULT_LANGUAGE)
		if (fallback) {
			const el = document.createElement('link')
			el.setAttribute('rel', 'alternate')
			el.setAttribute('hreflang', 'x-default')
			el.setAttribute('href', absolute(fallback.path))
			undos.push(appendFresh(el))
		}

		jsonLd.forEach(schema => {
			const el = document.createElement('script')
			el.setAttribute('type', 'application/ld+json')
			el.textContent = JSON.stringify(schema)
			undos.push(appendFresh(el))
		})

		document.documentElement.dataset.seoReady = path
		return () => {
			delete document.documentElement.dataset.seoReady
			undos.reverse().forEach(undo => undo())
		}
	}, [serialized])
}

export const buildAlternates = (
	pathForLang: (lang: SupportedLanguage) => string | null
): SeoAlternate[] =>
	SUPPORTED_LANGUAGES
		.map(lang => ({ lang, path: pathForLang(lang) }))
		.filter((entry): entry is SeoAlternate => entry.path !== null)

export default useSeo
